import { FormEvent, useRef, useState } from "react";

import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import Spark from "@/assets/icons/24/Spark";
import { ACCOUNT_SEARCHES_ROUTE } from "@/common/static/routes.static";
import { SearchDialog } from "@/features/search/components/saved-search/SearchDialog";
import { useSearchContext } from "@/features/search/context/SearchContext";
import {
  useFacetLabel,
  useFacetValueLabel,
} from "@/features/search/hooks/useFacetLabel";
import { useCreateSavedSearch } from "@/features/search/hooks/useSavedSearches";
import { getAppliedFilters } from "@/features/search/utils/applied-filters.utils";
import { getVisibleFacets } from "@/features/search/utils/facets.utils";
import { InfoPanel } from "@/ui/InfoPanel";
import { Button } from "@/ui/cva/Button";
import { CheckBox } from "@/ui/cva/check-box/CheckBox";
import { Input } from "@/ui/cva/input/Input";
import { DialogClose } from "@radix-ui/react-dialog";

type SaveSearchModalProps = {
  /** The search to save, as a `/search` query string — see `buildSavedSearchParams`. */
  params: string;
  onSaved: () => void;
};

/**
 * Save-search dialog: name it, choose whether it alerts, save.
 *
 * Success replaces the dialog's own body rather than opening a second modal — one dialog, two
 * states, so nothing has to hand control to another overlay mid-flow.
 *
 * There is no frequency control: the service stores a single `alerts` boolean, and the schedule is
 * fixed on its side. A dropdown here would be a promise the API can't keep.
 */
export const SaveSearchModal = ({ params, onSaved }: SaveSearchModalProps) => {
  const { t } = useTranslation();
  const { locale, query: routerQuery } = useRouter();
  const { query } = useSearchContext();
  const facetLabel = useFacetLabel();
  const facetValueLabel = useFacetValueLabel();

  /*
   * The service names the search — "Search for teak dining tables in Netherlands", "Search from €300"
   * — which is both what the dashboard will show and what it would generate for a blank title, so the
   * suggestion and the empty field can't disagree. Composing a name here instead would mean
   * re-deriving labels the service already resolved, in a phrasing only it owns.
   *
   * The timestamp is the last resort, for a search with nothing nameable in it at all.
   *
   * Computed once, on open (the dialog remounts per open, so it always reflects the current filters):
   * a timestamp that ticked while it was up would rewrite what the member is editing.
   */
  const [title, setTitle] = useState(
    () =>
      query.data?.meta.suggestedTitle ??
      t("listing:save-search-default-name", {
        datetime: formatNow(locale),
        defaultValue: "Saved search - {{datetime}}",
        /* i18next HTML-escapes interpolated values, which turned the date's slashes into
         * `8&#x2F;6&#x2F;26` inside the input. Off here because the value is a locale-formatted date
         * we produced ourselves, and React escapes it again on render anyway. */
        interpolation: { escapeValue: false },
      })
  );
  const [alerts, setAlerts] = useState(true);
  const [saved, setSaved] = useState(false);
  const preselected = useRef(false);

  const { mutate, isPending, isError } = useCreateSavedSearch();

  const freeText =
    typeof routerQuery.query === "string" ? routerQuery.query : "";
  const appliedFilters = getAppliedFilters(
    getVisibleFacets(query.data?.facets ?? []),
    facetLabel,
    facetValueLabel,
    { from: t("common:common-from"), to: t("common:common-to") }
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    mutate(
      {
        params,
        /* Left out entirely when blank: the service generates a title describing the filters,
         * which beats storing an empty string it would then have to paper over on read. */
        ...(title.trim() ? { title: title.trim() } : {}),
        alerts,
        source: "web",
      },
      {
        onSuccess: () => {
          setSaved(true);
          onSaved();
        },
      }
    );
  };

  if (saved) {
    return (
      <SearchDialog
        icon={<Spark className="h-8 w-8 text-brand-500" />}
        title={t("listing:save-search-success-modal-title")}
        copy={t("listing:save-search-success-modal-desc")}
        closeLabel={t("common:common-close")}
        footer={
          <>
            <LinkButton
              href={ACCOUNT_SEARCHES_ROUTE}
              color="brand"
              appearance="solid"
              fill
            >
              {t("listing:save-search-success-modal-action1")}
            </LinkButton>
            <DialogClose asChild>
              <Button color="brand" appearance="outlined" fill>
                {t("listing:save-search-success-modal-action2")}
              </Button>
            </DialogClose>
          </>
        }
      />
    );
  }

  return (
    <SearchDialog
      icon={<Spark className="h-8 w-8 text-brand-500" />}
      title={t("listing:save-search-modal-title")}
      copy={t("listing:save-search-modal-text")}
      closeLabel={t("common:common-close")}
      footer={
        <>
          <Button
            type="submit"
            form={FORM_ID}
            color="brand"
            appearance="solid"
            fill
            loading={isPending}
          >
            {t("listing:save-search-modal-action")}
          </Button>
          <DialogClose asChild>
            <Button color="brand" appearance="outlined" fill>
              {t("common:common-cancel")}
            </Button>
          </DialogClose>
        </>
      }
    >
      {/* The footer sits outside the form (it belongs to the dialog shell), so submit reaches it
       * through `form=` rather than by nesting the buttons. */}
      <form
        id={FORM_ID}
        onSubmit={handleSubmit}
        className="flex flex-col space-y-4"
      >
        <div className="flex flex-col space-y-1">
          <label
            htmlFor={TITLE_ID}
            className="font-title text-base leading-6 text-black"
          >
            {t("listing:save-search-name-label", { defaultValue: "Name" })}
          </label>
          <Input
            id={TITLE_ID}
            value={title}
            maxLength={120}
            autoFocus
            placeholder={t("listing:save-search-name-placeholder", {
              defaultValue: "Name this search",
            })}
            onChange={event => setTitle(event.target.value)}
            /* Selected once, so typing replaces the generated name outright. Guarded because a
             * later click into the field means "put the cursor here", not "start over". */
            onFocus={event => {
              if (!preselected.current) {
                preselected.current = true;
                event.currentTarget.select();
              }
            }}
          />
        </div>

        {/* What is actually being saved. The filters live in a sidebar, a mobile panel and the
         * URL — none of which is in view once the dialog covers the page, so without this the
         * member is naming something they can no longer see. */}
        <section className="flex flex-col space-y-2">
          <h3 className="font-title text-sm font-bold leading-5 text-black">
            {t("listing:save-search-summary-title", {
              defaultValue: "What you're saving",
            })}
          </h3>

          {/* `listing` and `common`, not `account-searches`: the results page only loads those
           * two, and a key from a namespace it never fetched renders as the key itself. */}
          <p className="font-body text-sm leading-5 text-grey-800">
            {t("listing:listing-search-words")} {freeText || t("common:none")}
          </p>

          {appliedFilters.length > 0 && (
            /* Negative-margin form, so wrapped rows keep both their spacings — see ActiveFilters. */
            <ul className="-ml-2 -mt-2 flex flex-wrap space-x-2 space-y-2 [&>:first-child]:ml-2 [&>:first-child]:mt-2">
              {appliedFilters.map(filter => (
                <li
                  key={filter.key}
                  /* Same Label treatment as the dashboard card — a static description of the
                   * search, not a chip that removes anything. */
                  className="rounded-[4px] bg-flavour-100 px-2 py-0.5 font-title text-sm leading-5 text-brand-600"
                >
                  {filter.label}
                </li>
              ))}
            </ul>
          )}
        </section>

        <label className="flex items-center space-x-2">
          <CheckBox
            checked={alerts}
            onChange={event => setAlerts(event.target.checked)}
          />
          <span className="font-body text-sm leading-5 text-grey-800">
            {t("listing:save-search-alerts-label", {
              defaultValue: "Email me when there are new matches",
            })}
          </span>
        </label>

        {isError && (
          <InfoPanel
            color="attention"
            body={
              <span className="font-title text-sm leading-5">
                {t("common:common_generic_error_message")}
              </span>
            }
          />
        )}
      </form>
    </SearchDialog>
  );
};

const FORM_ID = "save-search-form";
const TITLE_ID = "save-search-title";

/**
 * "12/08/2026, 14:05" in the member's own locale. Client-only — the dialog never renders on the
 * server, so there is no timezone-dependent hydration mismatch to worry about.
 */
const formatNow = (locale?: string) =>
  new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date());
